import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { X, UserPlus, Trash2, Crown } from "lucide-react";

/**
 * LinkedUsersDialog — manage which user accounts can sign-in and access a
 * specific client. There is always exactly one *primary* user (the account
 * originally created with the client) plus an arbitrary number of additional
 * (secondary) users linked by email. Secondary users keep their own primary
 * client but gain read/write access to this one too.
 */
export default function LinkedUsersDialog({ client, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clients/${client.id}/users`);
      setUsers(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, [client?.id]);

  const addUser = async (e) => {
    e.preventDefault();
    setError("");
    const v = email.trim().toLowerCase();
    if (!v) return;
    setAdding(true);
    try {
      await api.post(`/clients/${client.id}/users`, { email: v });
      setEmail("");
      await reload();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
    setAdding(false);
  };

  const removeUser = async (u) => {
    setError("");
    if (u.is_primary) return;
    if (!window.confirm(`Rimuovere ${u.email} dall'accesso a ${client.name}?`)) return;
    try {
      await api.delete(`/clients/${client.id}/users/${u.id}`);
      await reload();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="linked-users-dialog"
    >
      <div className="bru-card w-full max-w-lg p-6 mt-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="tag-label">UTENTI · ACCESSO</div>
            <h3 className="text-2xl font-bold">
              Accessi · <span className="text-[#ff5a00]">{client.name}</span>
            </h3>
            <p className="text-xs text-neutral-500 mt-1">
              Aggiungi un&apos;email esistente per concedergli l&apos;accesso a questo cliente.
            </p>
          </div>
          <button onClick={onClose} className="bru-btn bru-btn-ghost !px-2 !py-1" data-testid="close-linked-users-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={addUser} className="flex gap-2 mb-4" data-testid="link-user-form">
          <input
            type="email"
            required
            placeholder="email@cliente.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bru-input flex-1"
            data-testid="link-user-email-input"
          />
          <button
            type="submit"
            disabled={adding}
            className="bru-btn bru-btn-primary disabled:opacity-60"
            data-testid="link-user-submit"
          >
            <UserPlus className="w-4 h-4" />
            {adding ? "..." : "Aggiungi"}
          </button>
        </form>

        {error && (
          <div role="alert" className="border-[1.5px] border-black bg-[#ff3b30] text-white px-3 py-2 text-sm mb-3" data-testid="linked-users-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="tag-label p-4 text-center">CARICAMENTO…</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-neutral-500 p-4 text-center">Nessun utente collegato.</div>
        ) : (
          <ul className="space-y-2" data-testid="linked-users-list">
            {users.map((u) => (
              <li
                key={u.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg border border-neutral-200 bg-neutral-50"
                data-testid={`linked-user-${u.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm truncate">{u.email}</div>
                  <div className="text-[10px] text-neutral-500 truncate flex items-center gap-1">
                    {u.is_primary ? (
                      <>
                        <Crown className="w-3 h-3 text-[#ff5a00]" />
                        Utente primario · creato con il cliente
                      </>
                    ) : (
                      <>Utente collegato · accesso secondario</>
                    )}
                  </div>
                </div>
                {!u.is_primary && (
                  <button
                    type="button"
                    onClick={() => removeUser(u)}
                    className="text-neutral-500 hover:text-[#ff3b30]"
                    title="Disassocia"
                    data-testid={`unlink-user-${u.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end mt-5">
          <button type="button" onClick={onClose} className="bru-btn bru-btn-secondary" data-testid="close-linked-users-button">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
